import type { FormacionPrecio, MotivoSinMayoristas } from './types';
import { fmt, fmtCosto } from './formato';
import { SemaforoRentabilidad } from './ui';

// Precios mayoristas (spec §4): el descuento por cantidad es lo que se ahorra de envío por unidad.

export function textoMotivo(motivo: MotivoSinMayoristas, envioGratisDesde: number | null): string {
  switch (motivo) {
    case 'sin_tabla': return 'Sin precios mayoristas: hace falta la tabla de costos de Mercado Libre (solapa Parámetros).';
    case 'sin_precio': return 'Sin precios mayoristas: primero tiene que haber precio sugerido (falta el costo o la rentabilidad deseada, o hay que revisar los parámetros: mirá las marcas).';
    case 'sin_peso': return 'Sin precios mayoristas: falta el peso (el descuento sale del ahorro de envío, que depende del peso).';
    case 'sin_envio_gratis':
      return `Sin precios mayoristas: sólo aplican con envío gratis (precio desde ${envioGratisDesde != null ? fmt(envioGratisDesde) : 'el envío gratis'}). `
        + 'Debajo, el envío lo paga el comprador y el precio por cantidad lo decidís vos.';
    case 'sin_cantidades': return 'Sin precios mayoristas: no hay cantidades cargadas en la solapa Parámetros.';
    case 'sin_tarifa_peso': return 'Sin precios mayoristas: las órdenes pesan más que la última fila de la tabla de costos de Mercado Libre.';
  }
}

export const AYUDA_MAYORISTAS = 'Vendiendo varias unidades juntas, Mercado Libre cobra un solo envío por la orden: lo que se ahorra de envío por unidad '
  + 'se descuenta del precio, así vos cobrás lo mismo por unidad. El precio se redondea para arriba y nunca pasa el precio de a una.';

export function PreciosMayoristas({ c }: { c: FormacionPrecio }) {
  if (!c.mayoristas) return null; // backend anterior (durante un deploy)
  const { motivo, tramos, sinTarifaPeso = [] } = c.mayoristas;
  if (motivo) return <p className="text-sm text-slate-500">{textoMotivo(motivo, c.envioGratisDesde)}</p>;
  const objetivo = c.rentabilidadPct ?? 0;
  return (
    <>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
            <th className="px-3 py-2">Cantidad</th>
            <th className="px-3 py-2 text-right">Precio por unidad</th>
            <th className="px-3 py-2 text-right">Ahorro de envío por unidad</th>
            <th className="px-3 py-2">Rentabilidad neta</th>
          </tr>
        </thead>
        <tbody>
          {tramos.map(t => (
            <tr key={t.cantidad} className="border-t border-slate-100">
              <td className="px-3 py-2">Desde {t.cantidad} unidades</td>
              <td className="px-3 py-2 text-right font-mono">{fmt(t.precio)}</td>
              <td className="px-3 py-2 text-right font-mono text-slate-600">{t.tope ? 'sin ahorro' : fmtCosto(t.ahorroPorUnidad)}</td>
              <td className="px-3 py-2">
                <span className="flex flex-wrap items-center gap-2">
                  <SemaforoRentabilidad netaPct={t.rentabilidadNeta.pctSobreCosto} objetivoPct={objetivo} />
                  {t.pierde && (
                    <span title="A este precio se pierde plata: el envío de la orden, dividido las unidades, sale más caro que el de una sola."
                      className="rounded-full bg-red-600 px-2 py-0.5 text-[11px] font-medium text-white">Pierde plata</span>
                  )}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {sinTarifaPeso.length > 0 && (
        <p className="mt-2 text-xs text-slate-500">
          Sin tarifa para el peso de la orden: desde {sinTarifaPeso.join(', ')} unidades (pesa más que la última fila de la tabla de costos de Mercado Libre).
        </p>
      )}
    </>
  );
}
